import { useState } from "react";
import { FieldPath, UseFormReturn } from "react-hook-form";

import { SaveIndicator } from "./SaveIndicator";
import { useSaveIndicator } from "@/hooks/useSaveIndicator";
import { InfoTooltip } from "@/components/form/InfoTooltip";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

import { cn } from "@/lib/utils";

export const AutosaveFormTextInput = <T extends Record<string, unknown>>({
  className,
  fieldName,
  form,
  infoHint,
  infoTooltip,
  label,
  onBlur,
  placeholder,
}: {
  className?: string;
  fieldName: FieldPath<T>;
  form: UseFormReturn<T>;
  infoHint?: string;
  infoTooltip?: string;
  label?: string;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => Promise<void> | void;
  placeholder?: string;
}) => {
  const { handleChange, status } = useSaveIndicator();
  const [isFocused, setIsFocused] = useState(false);

  const infoHintLines = infoHint?.split("\n").filter(Boolean);
  const showHintBox = isFocused && !!infoHintLines;

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={({ field }) => {
        return (
          <FormItem className={cn("text-slate-700", className)}>
            {label && (
              <FormLabel className="inline-flex w-full items-center justify-between">
                <span className="inline-flex items-center gap-1">
                  {label}
                  {infoTooltip && <InfoTooltip content={infoTooltip} />}
                </span>
                <SaveIndicator status={status} />
              </FormLabel>
            )}

            <FormControl>
              <Input
                {...field}
                className="bg-slate-50 text-black"
                onChange={(e) => {
                  field.onChange(e);
                  handleChange();
                }}
                onBlur={(e) => {
                  setIsFocused(false);
                  field.onBlur();
                  void onBlur(e);
                }}
                onFocus={() => {
                  setIsFocused(true);
                }}
                placeholder={placeholder ?? "-"}
                value={field.value as string}
              />
            </FormControl>
            {showHintBox && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <p className="text-sm text-slate-400">
                  {infoHintLines.map((line) => line.trim()).join(", ")}
                </p>
              </div>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};
